import {
  ApartmentOutlined,
  ArrowRightOutlined,
  DatabaseOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Button, Card, Space, Tag, Typography, theme } from 'antd';
import type { CatalogCategoryResponse } from '@aid/shared';

const { Text, Title } = Typography;

interface CatalogCategoryCardProps {
  category: CatalogCategoryResponse;
  onOpen: (code: string) => void;
  onEdit?: (code: string) => void;
}

export function CatalogCategoryCard({ category, onOpen, onEdit }: CatalogCategoryCardProps) {
  const { token } = theme.useToken();
  const hierarchical = category.behavior.hierarchy.enabled;

  return (
    <Card
      hoverable
      role="link"
      tabIndex={0}
      onClick={() => onOpen(category.code)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(category.code);
        }
      }}
      style={{
        height: '100%',
        borderRadius: 14,
        borderColor: token.colorBorderSecondary,
      }}
      styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%', gap: 14 } }}
    >
      <Space align="start" size={12}>
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 38,
            height: 38,
            borderRadius: 10,
            color: token.colorPrimary,
            background: token.colorPrimaryBg,
            fontSize: 18,
            flex: '0 0 auto',
          }}
        >
          {hierarchical ? <ApartmentOutlined /> : <DatabaseOutlined />}
        </span>
        <div style={{ minWidth: 0 }}>
          <Title level={5} style={{ margin: 0 }}>
            {category.name}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {category.code}
          </Text>
        </div>
      </Space>

      <Text type="secondary" style={{ minHeight: 40 }}>
        {category.description ?? 'Valores configurables del sistema.'}
      </Text>

      <Space wrap size={[6, 6]}>
        <Tag>{category.itemCount} registros</Tag>
        {category.activeItemCount !== category.itemCount && (
          <Tag color="warning">{category.activeItemCount} activos</Tag>
        )}
        {hierarchical && <Tag color="blue">Jerárquico</Tag>}
      </Space>

      <Space wrap size={[4, 4]} style={{ marginTop: 'auto' }}>
        <Button
          type="link"
          icon={<ArrowRightOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            onOpen(category.code);
          }}
          style={{ paddingInline: 0 }}
        >
          Administrar categoría
        </Button>
        {onEdit && (
          <Button
            type="text"
            icon={<EditOutlined />}
            aria-label={`Editar ${category.name}`}
            onClick={(event) => {
              event.stopPropagation();
              onEdit(category.code);
            }}
          />
        )}
      </Space>
    </Card>
  );
}
